import React from 'react';
import { SafeMarkdown } from './SafeMarkdown.js';
import { BookOpen } from 'lucide-react';
import { Sheet } from './Sheet.js';
import { MermaidBlock } from './MermaidBlock.js';

interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  markdown: string;
}

/** Adds keys to all children of a rendered element — fixes markdown-to-jsx missing keys */
const withKeyedChildren = (Tag: string) => {
  const Component: React.FC<React.HTMLAttributes<HTMLElement>> = ({ children, ...props }) =>
    React.createElement(
      Tag,
      props,
      React.Children.map(children, (child, i) =>
        React.isValidElement(child) ? React.cloneElement(child, { key: child.key ?? i }) : child,
      ),
    );
  return Component;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const KeyedTable = withKeyedChildren('table');
const KeyedThead = withKeyedChildren('thead');
const KeyedTbody = withKeyedChildren('tbody');

export const markdownOverrides = {
  h1: { props: { className: 'text-xl font-bold mb-3 mt-5 text-heading first:mt-0' } },
  h2: { props: { className: 'text-lg font-semibold mb-2 mt-4 text-heading first:mt-0' } },
  h3: { props: { className: 'text-base font-semibold mb-2 mt-3 text-heading' } },
  h4: { props: { className: 'text-sm font-semibold mb-1.5 mt-2 text-heading' } },
  p: { props: { className: 'mb-2 leading-relaxed text-body text-sm' } },
  ul: { props: { className: 'list-disc pl-5 mb-2 space-y-1 text-sm text-body' } },
  ol: { props: { className: 'list-decimal pl-5 mb-2 space-y-1 text-sm text-body' } },
  li: { props: { className: 'text-sm' } },
  code: { props: { className: 'bg-surface-alt px-1.5 py-0.5 rounded-sm text-xs font-mono text-heading' } },
  pre: {
    component: ({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) => {
      // Detect mermaid code blocks: <pre><code class="lang-mermaid">...</code></pre>
      const child = React.Children.toArray(children)[0];
      const childProps = React.isValidElement(child) ? child.props : undefined;
      if (isRecord(childProps) && childProps.className === 'lang-mermaid') {
        const code = childProps.children;
        if (typeof code === 'string') return <MermaidBlock>{code}</MermaidBlock>;
      }
      return (
        <pre className="bg-surface-alt p-3 rounded-lg overflow-auto text-xs font-mono my-2" {...props}>
          {children}
        </pre>
      );
    },
  },
  strong: { props: { className: 'font-semibold text-heading' } },
  em: { props: { className: 'italic' } },
  blockquote: { props: { className: 'border-l-4 border pl-4 italic text-muted my-2' } },
  table: { component: KeyedTable, props: { className: 'border-collapse w-full text-sm my-2' } },
  thead: { component: KeyedThead },
  tbody: { component: KeyedTbody },
  tr: { props: { className: '' } },
  th: { props: { className: 'border border px-3 py-1.5 bg-bg text-left font-medium text-body' } },
  td: { props: { className: 'border border px-3 py-1.5 text-body' } },
  hr: { props: { className: 'my-4 border-t' } },
};

/** The page's help, from `client/src/help/*.md`, in a side panel. */
export const HelpPanel: React.FC<HelpPanelProps> = ({ open, onClose, title, markdown }) => (
  <Sheet
    open={open}
    onClose={onClose}
    title={title}
    titleIcon={<BookOpen size={16} className="text-accent-text" aria-hidden="true" />}
    width="lg"
  >
    <SafeMarkdown forceBlock overrides={markdownOverrides}>
      {markdown}
    </SafeMarkdown>
  </Sheet>
);
